import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import TagsInput from 'react-tagsinput';
import locales from '../../../../../../locales';

import uploadFile, {getFileType} from '../../../../../../utils/FileService';
import projectConfig from '../../../../../../config';
import Translate, {t} from '../../../../../../components/Translate';
import {getFileNameFromLink, processNodeButton} from '../../util';
import config from '../../config';
import LoadingComponent from "../../../../../../components-new/ui/LoadingComponent";
import './styles.css'
import SilverButton from "../../../../../../components-new/buttons/SilverButton";
import BlueButton from "../../../../../../components-new/buttons/BlueButton";
import DeleteIcon from "../../../../../../assets/icons/DeleteIcon";
import colors from "../../../../../../utils/Colors";
import ResetButton from "../../../../../../components-new/buttons/ResetButton";
import NodeEditorHeader from "./NodeEditorHeader";
import Accordion from "../../../../../../components-new/ui/Accordion";
import AccordionItem from "../../../../../../components-new/ui/Accordion/AccordionItem";
import PlusIcon from "../../../../../../assets/icons/PlusIcon";
import TagIcon from "../../../../../../assets/icons/TagIcon";
import PaperClipIcon from "../../../../../../assets/icons/PaperClipIcon";
import CopyPlusIcon from "../../../../../../assets/icons/CopyPlusIcon";
import WorkFlowIcon from "../../../../../../assets/icons/WorkflowIcon";
import MinusIcon from "../../../../../../assets/icons/MinusIcon";
import Messages from "./partials/Messages";
import Attachment from "../../../../../../components-new/general/Attachment";
import Actions from "./partials/Actions";
import Buttons from "./partials/Buttons";
import NodeTypePicker from "./partials/NodeTypePicker";
import WorkspacesIcon from "../../../../../../assets/icons/WorkspacesIcon";
import DestructiveButton from "../../../../../../components-new/buttons/DestructiveButton";
import ForumIcon from "../../../../../../assets/icons/ForumIcon";

const NodeEditor = ({
                        node,
                        editingNode,
                        deletingNode,
                        editNode,
                        deleteNode,
                        addNotification,
                        user,
                        company,
                        initialFlags = [],
                        onClose,
                        isFetching,
                        tariff,
                        hasChildren = false
                    }) => {
    const [visible, setVisible] = useState(false);
    const [dragIsActive, setDragIsActive] = useState(false);
    const [id, setId] = useState(null);
    const [name, setName] = useState('');
    const [messages, setMessages] = useState([]);
    const [buttons, setButtons] = useState([]);
    const [tags, setTags] = useState([]);
    const [botAttachments, setBotAttachments] = useState([]);
    const [terminalMessage, setTerminalMessage] = useState(false);
    const [chatWithOperator, setChatWithOperator] = useState(false);
    const [callToOperator, setCallToOperator] = useState(false);
    const [toStart, setToStart] = useState(false);
    const [nodeType, setNodeType] = useState(config.nodeTypes.message);
    const [outOfHoursText, setOutOfHoursText] = useState('');
    const [snapshot, setSnapshot] = useState(null);
    const [accordionDefaultValue, setAccordionDefaultValue] = useState(['s-1']);
    const [newAttachments, setNewAttachments] = useState([]);

    const message = useMemo(() => {
        return messages.join('/n');
    }, [messages]);

    const isDirty = useMemo(() => {
        if (!snapshot) return false;

        const shallowEqualArray = (a, b) =>
            a.length === b.length && a.every((v, i) => JSON.stringify(v) === JSON.stringify(b[i]));

        return !(
            snapshot.name === name &&
            shallowEqualArray(snapshot.messages, messages) &&
            shallowEqualArray(snapshot.buttons, buttons) &&
            shallowEqualArray(snapshot.tags, tags) &&
            shallowEqualArray(snapshot.botAttachments, botAttachments) &&
            snapshot.terminalMessage === terminalMessage &&
            snapshot.chatWithOperator === chatWithOperator &&
            snapshot.callToOperator === callToOperator &&
            snapshot.toStart === toStart &&
            snapshot.nodeType === nodeType &&
            snapshot.outOfHoursText === outOfHoursText &&
            newAttachments.length === 0
        );
    }, [
        snapshot,
        name,
        messages,
        buttons,
        tags,
        botAttachments,
        terminalMessage,
        chatWithOperator,
        toStart,
        callToOperator,
        nodeType,
        outOfHoursText,
        newAttachments
    ]);


    useEffect(() => {
        requestAnimationFrame(() => setVisible(true));
        const accordionValues = []

        if (initialFlags.includes('addButton')) {
            accordionValues.push('s-4');
        }
        if (initialFlags.includes('messages')) {
            accordionValues.push('s-1');
        }
        if (initialFlags.includes('attachments')) {
            accordionValues.push('s-3');
        }

        setAccordionDefaultValue(accordionValues);

        return () => {
            setVisible(false)
        };
    }, []);

    useEffect(() => {
        if (!node) return;
        setId(node.id);
        setName(node.name || '');
        setMessages(node.message ? node.message.split('/n') : []);
        setButtons(node.buttons.map(processNodeButton) || []);
        setTags(node.tags || []);
        setBotAttachments(node.botAttachments || []);
        setTerminalMessage(node.terminalMessage || false);
        setChatWithOperator(node.chatWithOperator || false);
        setCallToOperator(node.callToOperator || false);
        setToStart(node.toStart || false);
        setNodeType(node.nodeType || config.nodeTypes.message);
        setOutOfHoursText(node.outOfHoursText || '');

        setSnapshot({
            id: node.id,
            name: node.name || '',
            messages: node.message ? node.message.split('/n') : [],
            buttons: node.buttons.map(processNodeButton) || [],
            tags: node.tags || [],
            botAttachments: node.botAttachments || [],
            terminalMessage: node.terminalMessage || false,
            chatWithOperator: node.chatWithOperator || false,
            callToOperator: node.callToOperator || false,
            toStart: node.toStart || false,
            nodeType: node.nodeType || config.nodeTypes.message,
            outOfHoursText: node.outOfHoursText || '',
        });
    }, [node]);

    const addButton = (defaultEditable = false) => {
        if (buttons.length >= config.nodeData.maxButtons) {
            addNotification('maxNumberOfButtonsIs', projectConfig.notificationLevels.error);
            return;
        }

        let minIndex = -1;
        buttons.forEach((b) => {
            if (b.id <= minIndex) minIndex -= 1;
        });

        const newButton = {
            id: minIndex,
            text: `${locales[user.language].newButton}`,
            shortText: `${locales[user.language].newButton}`,
            defaultEditable: defaultEditable,
        };
        setButtons((prev) => [...prev, newButton]);
    };

    const handleNodeTagsChange = (newTags) => {
        setTags(newTags);
    };

    const addAttachment = (attachmentData) => {
        setBotAttachments((prev) => [...prev, attachmentData]);
    };

    const removeAttachment = (link) => () => {
        setBotAttachments((prev) => prev.filter((a) => a.link !== link));
    };

    const handleFileUpload = (files) => {
        setDragIsActive(false);

        setNewAttachments([...newAttachments, files[0]]);
    };

    const getNodeData = () => {
        const newButtons = buttons.map((b) => {
            const text = b.link ? `${b.shortText}||${b.link}` : b.shortText;
            return b.id > 0 ? {...b, text} : {text};
        });
        return {
            id,
            name,
            message,
            buttons: newButtons,
            terminalMessage,
            tags,
            botAttachments,
            chatWithOperator,
            callToOperator,
            toStart,
            nodeType,
            outOfHoursText
        };
    };

    const handleReset = useCallback(() => {
        if (!snapshot) return;
        setName(snapshot.name);
        setMessages(snapshot.messages);
        setButtons(snapshot.buttons);
        setTags(snapshot.tags);
        setBotAttachments(snapshot.botAttachments);
        setTerminalMessage(snapshot.terminalMessage);
        setChatWithOperator(snapshot.chatWithOperator);
        setCallToOperator(snapshot.callToOperator);
        setToStart(snapshot.toStart);
        setNodeType(snapshot.nodeType);
        setOutOfHoursText(snapshot.outOfHoursText);
        setNewAttachments([]);
    }, [snapshot]);

    /*
     * A node that hands the chat to an operator sends its attachments and its
     * text, and then hands over. What it has no use for is hidden: buttons it
     * never sends, the four actions it never runs, and tags, which exist to
     * match what a person typed against a node they could have reached.
     *
     * Hidden rather than disabled - a panel full of controls that do nothing is
     * a panel that has to be explained, and there is nothing to explain once the
     * sections are simply not there.
     *
     * Nothing already saved is cleared. Change the type back and the node is
     * exactly as it was.
     */
    const isTransferNode = nodeType === config.nodeTypes.operatorTransfer;

    /*
     * Changing the type renames the node, but only while nobody has named it.
     *
     * A node left as "New node" tells a reader of the tree nothing, and this is
     * the one moment we know something worth putting there. But the name is also
     * the whole point of this node type - "Sales", "Billing", the thing that
     * says which path this is - so a name somebody chose is never touched. Only
     * the two we write ourselves are swapped for each other.
     */
    const handleNodeTypeChange = (nextType) => {
        setNodeType(nextType);

        const ourNames = [t({text: 'newNode', user}), t({text: 'nodeTypeOperatorTransfer', user})];

        if (name && ourNames.indexOf(name.trim()) === -1) return;

        setName(nextType === config.nodeTypes.operatorTransfer
            ? t({text: 'nodeTypeOperatorTransfer', user})
            : t({text: 'newNode', user}));
    };

    const handleChildClick = (e) => e.stopPropagation();

    const addTagText = `${locales[user.language].addTag}`;

    const handleSave = () => {

        const uploadPromises = newAttachments.map((att) => {
            const data = new FormData();
            data.append('file', att);

            return uploadFile(data).then((link) => ({
                link,
                type: getFileType(att.type),
                name: att.name
            }));
        });

        Promise.all(uploadPromises).then((uploadedAttachments) => {

            const updatedAttachments = [...botAttachments, ...uploadedAttachments];
            setBotAttachments(updatedAttachments);

            const nodeData = {
                ...getNodeData(),
                botAttachments: updatedAttachments,
            };

            editNode(nodeData);
        }).catch((e) => {
            addNotification('failedToSaveNode', projectConfig.notificationLevels.error);
            console.error(e);
        });
    };


    return (
        <div className={'node-editor-container' + (visible ? ' open' : '')} onClick={handleChildClick}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '0',
                }}
            >
                <NodeEditorHeader
                    nodeName={name}
                    updateName={setName}
                    onClose={onClose}
                />

                <Accordion
                    key={accordionDefaultValue.join('-')} // for default value to work
                    type='multiple'
                    className='node-editor-accordion'
                    defaultValue={accordionDefaultValue}
                >
                    <AccordionItem
                        value='s-1'
                        triggerContent={
                            <div className="node-editor-accordion-item-trigger">
                                <ForumIcon width={18} height={18}/>
                                <Translate text='messages'/>
                            </div>
                        }
                        triggerIcon={({isOpen}) => {
                            return isOpen() ?
                                <MinusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                           height={18}/> :
                                <PlusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                          height={18}/>
                        }}
                        rotateIcon={false}
                    >
                        <Messages
                            messages={messages}
                            setMessages={setMessages}
                        />
                    </AccordionItem>
                    {!isTransferNode && <AccordionItem
                        value='s-2'
                        triggerContent={
                            <div className="node-editor-accordion-item-trigger">
                                <TagIcon width={18} height={18}/>
                                <Translate text='tags'/>
                            </div>
                        }
                        triggerIcon={({isOpen}) => {
                            return isOpen() ?
                                <MinusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                           height={18}/> :
                                <PlusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                          height={18}/>
                        }}
                        rotateIcon={false}
                    >
                        <div className="control-section">

                            <div className="tags-input controller">
                                <TagsInput
                                    addOnBlur
                                    value={tags}
                                    onChange={handleNodeTagsChange}
                                    inputProps={{placeholder: addTagText}}
                                />
                            </div>
                        </div>

                    </AccordionItem>}

                    <AccordionItem
                        value='s-3'
                        triggerContent={
                            <div className="node-editor-accordion-item-trigger">
                                <PaperClipIcon width={18} height={18}/>
                                <Translate text='attachments'/>
                            </div>
                        }
                        triggerIcon={({isOpen}) => {
                            return isOpen() ?
                                <MinusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                           height={18}/> :
                                <PlusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                          height={18}/>
                        }}
                        rotateIcon={false}
                    >
                        <div className="control-section">
                            <div className="attach-file" style={{display: dragIsActive ? 'none' : 'block'}}>
                                <Dropzone className="node-editor-dropzone" multiple={false} onDrop={handleFileUpload}>
                                    <SilverButton
                                        width={'100%'}
                                        height={'100%'}
                                        styles={{fontSize: '14px', color: colors.silverDark}}
                                        variant='ghost'
                                    >
                                        <Translate text="attachFile"/>
                                    </SilverButton>
                                </Dropzone>
                                <ul style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    padding: 0,
                                    margin: '10px 0',
                                }}>
                                    {newAttachments.map((a) => (
                                        <Attachment
                                            key={a.preview}
                                            link={a.preview}
                                            fileName={a.name}
                                            onDelete={() => setNewAttachments(newAttachments.filter(na => na.preview !== a.preview))}
                                        />
                                    ))}

                                    {botAttachments.map((a) => (
                                        <Attachment
                                            key={a.link}
                                            link={a.link}
                                            fileName={a.name ? a.name : getFileNameFromLink(a.link)}
                                            onDelete={removeAttachment(a.link)}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </div>

                    </AccordionItem>

                    {!isTransferNode && <AccordionItem
                        value='s-4'
                        triggerContent={
                            <div className="node-editor-accordion-item-trigger">
                                <CopyPlusIcon width={18} height={18}/>
                                <Translate text='buttons'/>
                            </div>
                        }
                        triggerIcon={({isOpen}) => {
                            return isOpen() ?
                                <MinusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                           height={18}/> :
                                <PlusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                          height={18}/>
                        }}
                        rotateIcon={false}
                    >
                        <Buttons
                            buttons={buttons}
                            addButton={addButton}
                            setButtons={setButtons}
                            addButtonOnMount={initialFlags.includes('addButton')}
                            showWhatsappNotice={company && company.tariff && company.tariff.messengers && company.tariff.messengers.includes(projectConfig.platformsMap.whatsapp)}
                        />
                    </AccordionItem>}

                    {!isTransferNode && <AccordionItem
                        value='s-5'
                        triggerContent={
                            <div className="node-editor-accordion-item-trigger">
                                <WorkFlowIcon width={18} height={18}/>
                                <Translate text='actions'/>
                            </div>
                        }
                        triggerIcon={({isOpen}) => {
                            return isOpen() ?
                                <MinusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                           height={18}/> :
                                <PlusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                          height={18}/>
                        }}
                        rotateIcon={false}
                    >
                        <Actions
                            terminalMessage={terminalMessage}
                            chatWithOperator={chatWithOperator}
                            callToOperator={callToOperator}
                            toStart={toStart}
                            setTerminalMessage={setTerminalMessage}
                            setChatWithOperator={setChatWithOperator}
                            setCallToOperator={setCallToOperator}
                            setToStart={setToStart}
                        />
                    </AccordionItem>}

                    {/* last, and the only section every kind of node has */}
                    <AccordionItem
                        value='s-6'
                        triggerContent={
                            <div className="node-editor-accordion-item-trigger">
                                <WorkspacesIcon width={18} height={18}/>
                                <Translate text='nodeType'/>
                            </div>
                        }
                        triggerIcon={({isOpen}) => {
                            return isOpen() ?
                                <MinusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                           height={18}/> :
                                <PlusIcon style={{color: colors.silverBlue}} stroke="currentColor" width={18}
                                          height={18}/>
                        }}
                        rotateIcon={false}
                    >
                        <NodeTypePicker
                            nodeType={nodeType}
                            setNodeType={handleNodeTypeChange}
                            hasChildren={hasChildren}
                            outOfHoursText={outOfHoursText}
                            setOutOfHoursText={setOutOfHoursText}
                            hasWorkingHours={
                                !!(tariff && tariff.features
                                    && tariff.features.includes(projectConfig.features.WORKING_HOURS))
                            }
                            user={user}
                        />
                    </AccordionItem>

                </Accordion>
            </div>


            <div style={{
                width: '100%',
                padding: '10px',
                background: 'var(--ds-bg-primary)',
                flex: '0 0 auto'
            }}>
                <div style={{
                    background: 'var(--ds-bg-primary)',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                }}>
                    <ResetButton
                        width={'auto'}
                        height={40}
                        showText={false}
                        style={{flex: 1}}
                        disabled={!isDirty || editingNode}
                        onClick={handleReset}
                    >
                        <Translate text="reset"/>
                    </ResetButton>
                    <DestructiveButton
                        onClick={() => deleteNode(node)}
                        styles={{flex: 1}}
                        height={40}
                        disabled={editingNode || node.isRoot}
                        variant='outline'
                    >
                        {
                            deletingNode ?
                                <LoadingComponent size={18}/>
                                :
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    color: colors.red,
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                }}>
                                    <DeleteIcon height={16} width={16} style={{color: colors.red}}
                                                stroke="currentColor"/>
                                    <Translate text="delete"/>
                                </div>
                        }
                    </DestructiveButton>
                    <BlueButton
                        onClick={handleSave}
                        disabled={deletingNode}
                        height={40}
                        styles={{fontSize: '14px', fontWeight: 'bold', flex: 1}}
                    >
                        {
                            editingNode ?
                                <LoadingComponent size={18} style={{color: colors.silver}} stroke="currentColor"/>
                                :
                                <Translate text="save"/>
                        }
                    </BlueButton>
                </div>
            </div>
        </div>
    );
};

NodeEditor.propTypes = {
    node: PropTypes.object.isRequired,
    editingNode: PropTypes.bool.isRequired,
    deletingNode: PropTypes.bool.isRequired,
    editNode: PropTypes.func.isRequired,
    deleteNode: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
};

export default NodeEditor;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/NodeEditor/index.jsx